/**
 * Template Service
 * TanStack Query hooks for template CRUD operations with Firestore
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';

// Query Keys
export const templateKeys = {
  all: ['templates'] as const,
  list: (filters?: TemplateFilters) => [...templateKeys.all, 'list', filters] as const,
  detail: (id: string) => [...templateKeys.all, 'detail', id] as const,
  byCategory: (category: string) => [...templateKeys.all, 'category', category] as const,
};

// Types
export type TemplateCategory =
  | 'real-estate'
  | 'estate-planning'
  | 'business'
  | 'litigation'
  | 'family-law'
  | 'other';

export interface Template {
  id: string;
  name: string;
  content: string;
  category: TemplateCategory;
  jurisdictions: string[];
  description?: string;
  tags?: string[];
  version: number;
  isPublished: boolean;
  createdAt: Date;
  createdBy: string;
  updatedAt: Date;
  updatedBy: string;
}

export interface TemplateFormData {
  name: string;
  content: string;
  category: TemplateCategory;
  jurisdictions: string[];
  description?: string;
  tags?: string[];
}

export interface TemplateFilters {
  category?: TemplateCategory;
  jurisdiction?: string;
  search?: string;
  isPublished?: boolean;
}

// Helper to convert Firestore timestamp to Date
function timestampToDate(timestamp: Timestamp | Date | undefined): Date {
  if (!timestamp) return new Date();
  if (timestamp instanceof Date) return timestamp;
  return timestamp.toDate();
}

// Generate unique ID
function generateId(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return `tpl_${timestamp}${random}`;
}

// Mock data for development
const mockTemplates: Template[] = [
  {
    id: 'tpl-1',
    name: 'Warranty Deed',
    content: 'WISCONSIN WARRANTY DEED\n\nThis Warranty Deed, made this {{executionDate}},\n\nBETWEEN {{grantor.firstName}} {{grantor.middleName}} {{grantor.lastName}}\n("Grantor")\n\nAND {{grantee.firstName}} {{grantee.middleName}} {{grantee.lastName}}\n("Grantee")\n\nWITNESSETH: That Grantor, for and in consideration of the sum of\n${{consideration}} and other good and valuable consideration...',
    category: 'real-estate',
    jurisdictions: ['WI'],
    description: 'Standard Wisconsin warranty deed template',
    tags: ['deed', 'warranty', 'transfer'],
    version: 1,
    isPublished: true,
    createdAt: new Date('2024-01-15'),
    createdBy: 'admin',
    updatedAt: new Date('2024-01-15'),
    updatedBy: 'admin',
  },
  {
    id: 'tpl-2',
    name: 'Simple Will',
    content: 'LAST WILL AND TESTAMENT OF {{testator.fullName}}\n\nI, {{testator.firstName}} {{testator.middleName}} {{testator.lastName}},\nof {{testator.address.city}}, {{testator.address.state}},\nbeing of sound mind and memory, do hereby declare this to be my Last Will and Testament...',
    category: 'estate-planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Basic simple will template with self-proving affidavit',
    tags: ['will', 'estate', 'testament', 'last will'],
    version: 1,
    isPublished: true,
    createdAt: new Date('2024-02-01'),
    createdBy: 'admin',
    updatedAt: new Date('2024-02-01'),
    updatedBy: 'admin',
  },
  {
    id: 'tpl-3',
    name: 'LLC Operating Agreement',
    content: 'OPERATING AGREEMENT OF {{company.name}}\n\nA {{company.state}} Limited Liability Company\n\nThis Operating Agreement is entered into as of {{effectiveDate}},\nby and among the Members executing this Agreement...',
    category: 'business',
    jurisdictions: ['WI', 'FL'],
    description: 'Multi-member LLC operating agreement',
    tags: ['llc', 'operating agreement', 'business formation'],
    version: 1,
    isPublished: true,
    createdAt: new Date('2024-03-10'),
    createdBy: 'admin',
    updatedAt: new Date('2024-03-10'),
    updatedBy: 'admin',
  },
  {
    id: 'tpl-4',
    name: 'Revocable Living Trust',
    content: 'REVOCABLE LIVING TRUST AGREEMENT OF {{grantor.fullName}}\n\nThis Revocable Living Trust Agreement is made this {{executionDate}},\nby and between {{grantor.firstName}} {{grantor.lastName}} ("Grantor" and "Trustee")...',
    category: 'estate-planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Comprehensive revocable living trust with pour-over provisions',
    tags: ['trust', 'revocable trust', 'living trust', 'estate planning'],
    version: 1,
    isPublished: true,
    createdAt: new Date('2024-03-15'),
    createdBy: 'admin',
    updatedAt: new Date('2024-03-15'),
    updatedBy: 'admin',
  },
  {
    id: 'tpl-5',
    name: 'Durable Power of Attorney',
    content: 'DURABLE POWER OF ATTORNEY\n\nI, {{principal.fullName}}, of {{principal.address.city}}, {{principal.address.state}},\nhereby appoint {{agent.fullName}} as my Attorney-in-Fact...',
    category: 'estate-planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Durable power of attorney for financial matters',
    tags: ['power of attorney', 'poa', 'financial', 'durable'],
    version: 1,
    isPublished: true,
    createdAt: new Date('2024-03-15'),
    createdBy: 'admin',
    updatedAt: new Date('2024-03-15'),
    updatedBy: 'admin',
  },
  {
    id: 'tpl-6',
    name: 'Health Care Directive',
    content: 'ADVANCE HEALTH CARE DIRECTIVE\n\nI, {{principal.fullName}}, being of sound mind, make this directive...',
    category: 'estate-planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Health care directive with living will and health care agent designation',
    tags: ['healthcare directive', 'living will', 'advance directive', 'medical poa'],
    version: 1,
    isPublished: true,
    createdAt: new Date('2024-03-15'),
    createdBy: 'admin',
    updatedAt: new Date('2024-03-15'),
    updatedBy: 'admin',
  },
  {
    id: 'tpl-7',
    name: 'Florida Warranty Deed',
    content: 'FLORIDA WARRANTY DEED\n\nThis Warranty Deed, made this {{executionDate}},\n\nBETWEEN {{grantor.fullName}}\n("Grantor")\n\nAND {{grantee.fullName}}\n("Grantee")...',
    category: 'real-estate',
    jurisdictions: ['FL'],
    description: 'Standard Florida warranty deed with documentary stamp calculation',
    tags: ['deed', 'warranty', 'transfer', 'florida'],
    version: 1,
    isPublished: true,
    createdAt: new Date('2024-03-20'),
    createdBy: 'admin',
    updatedAt: new Date('2024-03-20'),
    updatedBy: 'admin',
  },
  {
    id: 'tpl-8',
    name: 'Quitclaim Deed',
    content: 'QUITCLAIM DEED\n\nThis Quitclaim Deed, made this {{executionDate}},\n\nBETWEEN {{grantor.fullName}}\n("Grantor")\n\nAND {{grantee.fullName}}\n("Grantee")...',
    category: 'real-estate',
    jurisdictions: ['WI', 'FL'],
    description: 'Quitclaim deed for property transfers without warranty',
    tags: ['deed', 'quitclaim', 'transfer'],
    version: 1,
    isPublished: true,
    createdAt: new Date('2024-03-20'),
    createdBy: 'admin',
    updatedAt: new Date('2024-03-20'),
    updatedBy: 'admin',
  },
];

// API Functions
async function fetchTemplates(filters?: TemplateFilters): Promise<Template[]> {
  try {
    const constraints = [];

    if (filters?.category) {
      constraints.push(where('category', '==', filters.category));
    }
    if (filters?.jurisdiction) {
      constraints.push(where('jurisdictions', 'array-contains', filters.jurisdiction));
    }
    if (filters?.isPublished !== undefined) {
      constraints.push(where('isPublished', '==', filters.isPublished));
    }

    constraints.push(orderBy('updatedAt', 'desc'));

    const q = query(collection(db, 'templates'), ...constraints);
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      // Return mock data if no Firestore data
      let filtered = [...mockTemplates];
      if (filters?.category) {
        filtered = filtered.filter(t => t.category === filters.category);
      }
      if (filters?.jurisdiction) {
        filtered = filtered.filter(t => t.jurisdictions.includes(filters.jurisdiction!));
      }
      if (filters?.search) {
        const search = filters.search.toLowerCase();
        filtered = filtered.filter(t =>
          t.name.toLowerCase().includes(search) ||
          t.description?.toLowerCase().includes(search)
        );
      }
      return filtered;
    }

    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        createdAt: timestampToDate(data.createdAt),
        updatedAt: timestampToDate(data.updatedAt),
      } as Template;
    });
  } catch (error) {
    console.error('Error fetching templates:', error);
    return mockTemplates;
  }
}

async function fetchTemplate(id: string): Promise<Template | null> {
  try {
    const docSnap = await getDoc(doc(db, 'templates', id));

    if (!docSnap.exists()) {
      // Check mock data
      return mockTemplates.find(t => t.id === id) || null;
    }

    const data = docSnap.data();
    return {
      ...data,
      id: docSnap.id,
      createdAt: timestampToDate(data.createdAt),
      updatedAt: timestampToDate(data.updatedAt),
    } as Template;
  } catch (error) {
    console.error('Error fetching template:', error);
    return mockTemplates.find(t => t.id === id) || null;
  }
}

async function createTemplate(data: TemplateFormData, userId: string): Promise<Template> {
  const id = generateId();

  const templateData = {
    ...data,
    id,
    version: 1,
    isPublished: false,
    createdAt: serverTimestamp(),
    createdBy: userId,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  };

  try {
    await setDoc(doc(db, 'templates', id), templateData);

    return {
      ...data,
      id,
      version: 1,
      isPublished: false,
      createdAt: new Date(),
      createdBy: userId,
      updatedAt: new Date(),
      updatedBy: userId,
    };
  } catch (error) {
    console.error('Error creating template:', error);
    // Return local version on error
    return {
      ...data,
      id,
      version: 1,
      isPublished: false,
      createdAt: new Date(),
      createdBy: userId,
      updatedAt: new Date(),
      updatedBy: userId,
    };
  }
}

async function updateTemplate(
  id: string,
  data: Partial<TemplateFormData>,
  userId: string
): Promise<Template> {
  try {
    await updateDoc(doc(db, 'templates', id), {
      ...data,
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });

    const updated = await fetchTemplate(id);
    return updated!;
  } catch (error) {
    console.error('Error updating template:', error);
    throw error;
  }
}

async function deleteTemplate(id: string): Promise<void> {
  try {
    await deleteDoc(doc(db, 'templates', id));
  } catch (error) {
    console.error('Error deleting template:', error);
    throw error;
  }
}

async function publishTemplate(id: string, userId: string): Promise<Template> {
  try {
    await updateDoc(doc(db, 'templates', id), {
      isPublished: true,
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });

    const updated = await fetchTemplate(id);
    return updated!;
  } catch (error) {
    console.error('Error publishing template:', error);
    throw error;
  }
}

// =============================================================================
// QUERY HOOKS
// =============================================================================

/**
 * Fetch list of templates
 */
export function useTemplates(filters?: TemplateFilters) {
  return useQuery({
    queryKey: templateKeys.list(filters),
    queryFn: () => fetchTemplates(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Fetch a single template by ID
 */
export function useTemplate(id: string) {
  return useQuery({
    queryKey: templateKeys.detail(id),
    queryFn: () => fetchTemplate(id),
    enabled: !!id,
  });
}

/**
 * Fetch templates by category
 */
export function useTemplatesByCategory(category: TemplateCategory) {
  return useQuery({
    queryKey: templateKeys.byCategory(category),
    queryFn: () => fetchTemplates({ category }),
    staleTime: 1000 * 60 * 5,
  });
}

// =============================================================================
// MUTATION HOOKS
// =============================================================================

/**
 * Create a new template
 */
export function useCreateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, userId }: { data: TemplateFormData; userId: string }) =>
      createTemplate(data, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
    },
  });
}

/**
 * Update an existing template
 */
export function useUpdateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data, userId }: { id: string; data: Partial<TemplateFormData>; userId: string }) =>
      updateTemplate(id, data, userId),
    onSuccess: (updated) => {
      queryClient.setQueryData(templateKeys.detail(updated.id), updated);
      queryClient.invalidateQueries({ queryKey: templateKeys.list() });
    },
  });
}

/**
 * Delete a template
 */
export function useDeleteTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
    },
  });
}

/**
 * Publish a template
 */
export function usePublishTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      publishTemplate(id, userId),
    onSuccess: (updated) => {
      queryClient.setQueryData(templateKeys.detail(updated.id), updated);
      queryClient.invalidateQueries({ queryKey: templateKeys.list() });
    },
  });
}

export default {
  useTemplates,
  useTemplate,
  useTemplatesByCategory,
  useCreateTemplate,
  useUpdateTemplate,
  useDeleteTemplate,
  usePublishTemplate,
  templateKeys,
};
